/**
 * ProgressBar — the gold-bordered step tracker that sits in the top overlay
 * strip, one dot per building round. Geometry and palette are taken 1:1 from
 * the Figma design (City-building / Group 77272), authored for a 248×26 pill
 * centered at (640, 21) in the 1280×720 stage.
 *
 *   - pill:        #2B2B2B fill, 3px #FFBA07 border, radius pill
 *   - track line:  #808080, 5px, spans the first → last dot
 *   - fill line:   #FFCF56, 5px, extends up to the active dot
 *   - dots (r≈5.9): active #FFDC83, inactive #808080
 *
 * Call setStep(i) to light dots 0..i (0-based) and extend the fill line.
 */

const COLORS = {
  bg: 0x2b2b2b,
  border: 0xffba07,
  track: 0x808080,
  fill: 0xffcf56,
  activeDot: 0xffdc83,
  inactiveDot: 0x808080,
};

const DOT_COUNT = 6;
const DOT_RADIUS = 5.91;
const DOT_SPACING = 43;
const LINE_WIDTH = 5;

// Pill dims and the first dot's center, expressed relative to the pill center
// (Figma: pill left 516 / width 248 → center 640; first dot center x = 534.9).
const PILL_WIDTH = 248;
const PILL_HEIGHT = 26;
const PILL_RADIUS = 13;
const FIRST_DOT_X = 534.9 - 640;

export default class ProgressBar extends Phaser.GameObjects.Container {
  constructor(scene, x, y) {
    super(scene, x, y);
    scene.add.existing(this);

    // Pill background + border.
    const pill = scene.add.graphics();
    pill
      .fillStyle(COLORS.bg, 1)
      .fillRoundedRect(
        -PILL_WIDTH / 2,
        -PILL_HEIGHT / 2,
        PILL_WIDTH,
        PILL_HEIGHT,
        PILL_RADIUS,
      );
    pill
      .lineStyle(3, COLORS.border, 1)
      .strokeRoundedRect(
        -PILL_WIDTH / 2,
        -PILL_HEIGHT / 2,
        PILL_WIDTH,
        PILL_HEIGHT,
        PILL_RADIUS,
      );
    this.add(pill);

    this.dotXs = Array.from(
      { length: DOT_COUNT },
      (_, i) => FIRST_DOT_X + i * DOT_SPACING,
    );

    // Gray track spanning first → last dot (drawn under the dots).
    const track = scene.add.graphics();
    track.lineStyle(LINE_WIDTH, COLORS.track, 1);
    track.lineBetween(this.dotXs[0], 0, this.dotXs[DOT_COUNT - 1], 0);
    this.add(track);

    // Yellow fill line, redrawn each setStep().
    this.fillLine = scene.add.graphics();
    this.add(this.fillLine);

    this.dots = this.dotXs.map(dx => {
      const dot = scene.add.circle(dx, 0, DOT_RADIUS, COLORS.inactiveDot);
      this.add(dot);
      return dot;
    });

    this.setStep(0);
  }

  // Light dots 0..step (0-based) and extend the fill line to the active dot.
  setStep(step) {
    const active = Phaser.Math.Clamp(step, 0, DOT_COUNT - 1);

    this.dots.forEach((dot, i) => {
      dot.setFillStyle(i <= active ? COLORS.activeDot : COLORS.inactiveDot);
    });

    this.fillLine.clear();
    this.fillLine.lineStyle(LINE_WIDTH, COLORS.fill, 1);
    this.fillLine.lineBetween(this.dotXs[0], 0, this.dotXs[active], 0);
  }
}
